/**
 * @file    CrossProgramRollupMockDataBuilder.ts
 * @scope   SHARED
 * @summary Mock preview data for CPR, in the response shape `DynamicTable` renders.
 * @consumes CprSlice
 * @produces CrossProgramRollupMockDataBuilder, CprMockResponse
 * @sideEffects None (pure).
 * @notes   One merged row (the rollup) + one column per trend period, values '-'. CPR owns this
 *          builder; it shares nothing with the Enterprise mock (plan §8).
 *
 * TODO(ai): confirm with backend the real rollup response columns/rows (is there a per-contributor
 * breakdown row? a base-size row?) and mirror them here. Also replace `buildPeriodLabel` with
 * real date labels if the preview must match the dashboard exactly.
 * @notes   `WidgetToRender` feeds the CPR slice mirrored in `engineSlices[CPR_CHART_TYPE]`;
 *          `toSlice` stays defensive for previews rendered before the drawer mounts.
 */
import { CprSlice } from '../model';
import { CPR_INITIAL_STATE } from '../state/cprReducer';

type CprMockColumn = {
  displayName: string;
  field: string;
  isNumeric: boolean;
  isDate: boolean;
  isLink: boolean;
  link: string;
  idLink: string;
  isAudioLink: boolean;
  rootURL: null;
  isPercentage: boolean;
  colType: string;
  colName: string;
};

type CprMockCell = {
  value: string;
  isPercentage: boolean;
  significanceIndicator: boolean;
  valueDifference: number;
};

export type CprMockResponse = {
  data: {
    page: number;
    per_page: number;
    total: number;
    total_page: number;
    data: Record<string, string | number | CprMockCell>[];
    columns: CprMockColumn[];
    showTotals: boolean;
    baseSize: string;
    controlData: {
      defaultValueL1: { label: string; value: string };
      defaultValueL2: { label: string; value: string };
    };
  };
};

const DEFAULT_PERIODS = 3;

const column = (field: string, displayName: string, isNumeric: boolean): CprMockColumn => ({
  displayName,
  field,
  isNumeric,
  isDate: false,
  isLink: false,
  link: '-1',
  idLink: '-1',
  isAudioLink: false,
  rootURL: null,
  isPercentage: false,
  colType: '',
  colName: displayName,
});

const buildPeriodLabel = (index: number, timePeriodType?: string): string =>
  `${timePeriodType ?? 'Period'} ${index + 1}`;

/** WHY: callers may pass a non-CPR slice (see TODO above); never crash the preview. */
const toSlice = (value: unknown): CprSlice => {
  const candidate = value as Partial<CprSlice> | undefined;
  return candidate && Array.isArray(candidate.metrics)
    ? { ...CPR_INITIAL_STATE, ...candidate }
    : CPR_INITIAL_STATE;
};

export const CrossProgramRollupMockDataBuilder = {
  build(value: unknown): CprMockResponse {
    const slice = toSlice(value);
    const periods = slice.trend?.selectedValue || DEFAULT_PERIODS;
    const periodType = slice.trend?.timePeriodType;

    const programCount = slice.metrics[0]?.programs.length ?? 0;

    const columns = [column('field_1', 'Metric', false)];
    const row: Record<string, string | number | CprMockCell> = {
      id: 'cpr-mock-row',
      field_1: programCount ? `Rollup (${programCount} programs)` : 'Rollup',
      rowType: 'L2',
    };

    for (let i = 0; i < periods; i++) {
      const field = `field_${i + 2}`;
      columns.push(column(field, buildPeriodLabel(i, periodType), true));
      row[field] = {
        value: '-',
        isPercentage: false,
        significanceIndicator: false,
        valueDifference: 0,
      };
    }

    return {
      data: {
        page: 1,
        per_page: 1,
        total: 1,
        total_page: 1,
        data: [row],
        columns,
        showTotals: false,
        baseSize: '',
        controlData: {
          defaultValueL1: { label: '', value: '' },
          defaultValueL2: { label: '', value: '' },
        },
      },
    };
  },
};
